import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { AccessContoPostura, AccessPostura, AccessProblemaConto } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { CategoryBars, Donut } from '../charts'
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageSection,
  SectionedPage,
  Spinner,
  StatCard,
  formatDateTime,
} from '../common'
import { DataTable } from '../DataTable'

/**
 * CENSO ACCESS: the security posture of the user accounts («User Account
 * Security Posture»). An overview with the score and the charts, and one tab
 * per problem — why it matters, how to fix it, and the accounts that have it.
 * The data are the accounts' state as their systems tell it (the domain
 * controller: every account of the domain with the non-privileged users on),
 * crossed with the register's last activity (`access_posture_service`).
 */
export function VistaPostura() {
  const dati = useAsync(() => api.accessPosture(), [])
  const p = dati.data
  if (dati.loading && !p) return <Spinner label={t('Leggo le utenze…')} />
  if (!p) return <ErrorBanner error={dati.error} />
  if (p.totals.accounts === 0) {
    return (
      <EmptyState
        title={t('Nessuna utenza descritta dai sistemi.')}
        hint={t('Lo stato delle utenze lo mandano gli agenti aggiornati: il domain controller per il dominio, ogni macchina per i suoi amministratori locali, Entra ID per i ruoli.')}
      />
    )
  }
  const problemi = PROBLEMI()
  return (
    <>
      {!p.standard_users && (
        <div className="banner banner--info">
          {t('Utenti non privilegiati spenti: qui ci sono solo le utenze privilegiate. Accendili nelle Impostazioni per avere tutte le utenze del dominio e trovare gli account zombie.')}{' '}
          <Link to="/access/settings">{t('Impostazioni')}</Link>
        </div>
      )}
      <div className="stats">
        <StatCard label={t('Punteggio')} value={p.score == null ? '—' : `${p.score}/100`}
                  tone={p.score == null ? undefined : p.score >= 80 ? 'ok' : p.score >= 50 ? 'warn' : 'error'}
                  hint={t('utenze attive senza problemi, le privilegiate contano doppio')} />
        <StatCard label={t('Utenze attive')} value={p.totals.enabled}
                  hint={t('su {n} · {d} disattivate', { n: p.totals.accounts, d: p.totals.disabled })} to="#tutte" />
        <StatCard label={t('Privilegiate a rischio')} value={p.totals.privileged_at_risk}
                  tone={p.totals.privileged_at_risk ? 'error' : 'ok'}
                  hint={t('su {n} privilegiate attive', { n: p.totals.privileged })} to="#privilegiate" />
        {problemi.map((x) => (
          <StatCard key={x.id} label={x.titolo} value={p.issues[x.id] ?? 0}
                    tone={(p.issues[x.id] ?? 0) === 0 ? 'ok' : x.grave ? 'error' : 'warn'} to={`#${x.id}`} />
        ))}
      </div>

      <SectionedPage
        sections={[
          { id: 'panoramica', label: t('Panoramica') },
          ...problemi.map((x) => ({ id: x.id, label: `${x.titolo} (${p.issues[x.id] ?? 0})` })),
          { id: 'privilegiate', label: t('Privilegiate ({n})', { n: p.totals.privileged }) },
          { id: 'tutte', label: t('Tutte le utenze ({n})', { n: p.totals.accounts }) },
        ]}
      >
        <PageSection id="panoramica">
          <Panoramica p={p} />
        </PageSection>
        {problemi.map((x) => (
          <PageSection key={x.id} id={x.id}>
            <Card title={x.titolo}>
              <div className="postura__consiglio">
                <div>
                  <h4>{t('Perché conta')}</h4>
                  <p>{x.perche(p)}</p>
                </div>
                <div>
                  <h4>{t('Come migliorare')}</h4>
                  <ol>{x.come.map((c) => <li key={c}>{c}</li>)}</ol>
                </div>
              </div>
            </Card>
            <Card title={t('Utenze con questo problema')}>
              <TabellaConti righe={p.accounts.filter((r) => r.issues.includes(x.id))} />
            </Card>
          </PageSection>
        ))}
        <PageSection id="privilegiate">
          <Card title={t('Utenze privilegiate')}>
            <p className="muted" style={{ marginTop: 0 }}>
              {t('Le utenze nei gruppi che amministrano il dominio o le macchine, anche attraverso un gruppo annidato: un problema qui pesa il doppio. Tienile poche, nominali, con MFA, e separate dalle utenze di tutti i giorni.')}
            </p>
            <TabellaConti righe={p.accounts.filter((r) => r.privileged)} />
          </Card>
        </PageSection>
        <PageSection id="tutte">
          <Card title={t('Tutte le utenze')}>
            <TabellaConti righe={p.accounts} />
          </Card>
        </PageSection>
      </SectionedPage>
    </>
  )
}

function Panoramica({ p }: { p: AccessPostura }) {
  const attiveConProblemi = p.accounts.filter((r) => r.enabled !== false && r.issues.length > 0).length
  const fette = [
    { key: 'ok', label: t('attive senza problemi'), value: p.totals.enabled - attiveConProblemi, color: 'var(--ok)' },
    { key: 'problemi', label: t('attive con problemi'), value: attiveConProblemi, color: 'var(--warn)' },
    { key: 'disattivate', label: t('disattivate'), value: p.totals.disabled, color: 'var(--text-muted)' },
  ]
  const attivita: [string, string][] = [
    ['lt30', t('meno di 30 giorni')], ['30_90', t('30–90 giorni')], ['90_180', t('90–180 giorni')],
    ['180_365', t('180–365 giorni')], ['gt365', t('oltre un anno')], ['never', t('mai')],
  ]
  const password: [string, string][] = [
    ['lt90', t('meno di 90 giorni')], ['90_180', t('90–180 giorni')], ['180_365', t('180–365 giorni')],
    ['gt365', t('oltre un anno')], ['unknown', t('non nota')],
  ]
  const fonti: Record<string, string> = {
    dc: t('dominio (domain controller)'), local: t('account locali'), entra: t('Entra ID'), linux: t('Linux'), other: t('altro'),
  }
  return (
    <div className="postura__grafici">
      <Card title={t('Stato delle utenze')}>
        <Donut slices={fette} centreLabel={t('{n} utenze', { n: p.totals.accounts })} />
      </Card>
      <Card title={t('Ultimo accesso delle utenze attive')}>
        <BarreInOrdine voci={attivita.map(([k, l]) => ({ etichetta: l, valore: p.last_activity[k] ?? 0, tono: k === 'gt365' || k === 'never' || k === '180_365' ? 'warn' : undefined }))} />
        <p className="field__hint">{t('Oltre {n} giorni senza accessi un’utenza attiva è uno zombie (soglia delle Impostazioni).', { n: p.dormant_days })}</p>
      </Card>
      <Card title={t('Età della password delle utenze attive')}>
        <BarreInOrdine voci={password.map(([k, l]) => ({ etichetta: l, valore: p.password_age[k] ?? 0, tono: k === 'gt365' ? 'warn' : undefined }))} />
        <p className="field__hint">{t('{n} utenze attive hanno una password che non scade.', { n: p.issues.password_never_expires ?? 0 })}</p>
      </Card>
      <Card title={t('Problemi trovati')}>
        <CategoryBars data={PROBLEMI().map((x) => ({ key: x.id, label: x.titolo, value: p.issues[x.id] ?? 0 })).filter((c) => c.value > 0)}
                      emptyHint={t('Nessun problema: ottimo.')} />
      </Card>
      <Card title={t('Da dove arrivano le utenze')}>
        <CategoryBars data={Object.entries(p.by_source).map(([k, v]) => ({ key: k, label: fonti[k] ?? k, value: v }))} />
        <p className="field__hint">{t('Letto alle {ora}.', { ora: formatDateTime(p.generated_at) })}</p>
      </Card>
    </div>
  )
}

/** Horizontal bars in the given order (age buckets read left to right, not by size). */
function BarreInOrdine({ voci }: { voci: { etichetta: string; valore: number; tono?: 'warn' }[] }) {
  const max = Math.max(...voci.map((v) => v.valore), 1)
  return (
    <div className="bars">
      {voci.map((v) => (
        <div className="bars__row" key={v.etichetta}>
          <span className="bars__label" title={v.etichetta}>{v.etichetta}</span>
          <span className="bars__track">
            <span className={`bars__fill${v.tono ? ' bars__fill--warn' : ''}`} style={{ width: `${v.valore ? Math.max((v.valore / max) * 100, 0.8) : 0}%` }} />
          </span>
          <span className="bars__value">{v.valore.toLocaleString()}</span>
        </div>
      ))}
    </div>
  )
}

function TabellaConti({ righe }: { righe: AccessContoPostura[] }) {
  const etichette = Object.fromEntries(PROBLEMI().map((x) => [x.id, x.titolo])) as Record<string, string>
  if (righe.length === 0) return <p className="muted">{t('Nessuna utenza.')}</p>
  return (
    <DataTable rows={righe} rowKey={(r) => `${r.identity_id}|${r.key}`} conteggio={{ nome: t('utenze') }}
      initialSort={{ key: 'days_idle', dir: 'desc' }} columns={[
        { key: 'account', header: t('Utenza'), value: (r) => r.account,
          render: (r) => <><Link to={`/access/people/${r.identity_id}`}>{r.account}</Link>{r.full_name && <><br /><small className="muted">{r.full_name}</small></>}</> },
        { key: 'enabled', header: t('Stato'), filter: 'select', value: (r) => r.enabled === false ? t('disattivata') : r.locked ? t('bloccata') : t('attiva') },
        { key: 'days_idle', header: t('Inattiva da'), type: 'number', value: (r) => r.days_idle ?? -1,
          render: (r) => r.last_logon ? <span title={formatDateTime(r.last_logon)}>{t('{n} giorni', { n: r.days_idle ?? 0 })}</span> : <span className="muted">{t('mai')}</span> },
        { key: 'password_last_set', header: t('Ultimo cambio password'), type: 'date', value: (r) => r.password_last_set ?? '',
          render: (r) => <>{r.password_last_set ? formatDateTime(r.password_last_set) : '—'}{r.password_never_expires && <><br /><small className="eagle-motivo">{t('non scade')}</small></>}</> },
        { key: 'privileged_groups', header: t('Gruppi privilegiati'), value: (r) => r.privileged_groups.join(', ') },
        { key: 'groups', header: t('Gruppi'), type: 'number', value: (r) => r.groups.length + r.indirect_groups.length },
        { key: 'issues', header: t('Problemi'), value: (r) => r.issues.map((i) => etichette[i] ?? i).join(', ') },
        { key: 'source', header: t('Fonte'), filter: 'select', value: (r) => r.host ? `${r.source} · ${r.host}` : r.source },
      ]} />
  )
}

interface Problema {
  id: AccessProblemaConto
  titolo: string
  grave: boolean
  perche: (p: AccessPostura) => string
  come: string[]
}

function PROBLEMI(): Problema[] {
  return [
    {
      id: 'zombie', titolo: t('Utenze zombie'), grave: true,
      perche: (p) => t('Un’utenza attiva che nessuno usa da oltre {n} giorni è una porta lasciata aperta: se la sua password è debole, riusata o rubata, nessuno se ne accorge, perché nessuno si aspetta di vederla entrare. Sono spesso ex dipendenti, consulenti a fine incarico, account di prova.', { n: p.dormant_days }),
      come: [
        t('Chiedi al responsabile di ciascuna se serve ancora; senza risposta, disattivala.'),
        t('Disattiva subito, cancella dopo un periodo di attesa (per esempio 30 giorni): così un errore si recupera.'),
        t('Metti la disattivazione nella procedura di uscita del personale e di fine contratto dei fornitori.'),
        t('Per i servizi usa account di servizio gestiti (gMSA) invece di utenze personali lasciate attive.'),
        t('Nota: l’ultimo accesso è quello visto dal domain controller su cui gira l’agente, incrociato con il registro di CENSO.'),
      ],
    },
    {
      id: 'never_used', titolo: t('Mai usate'), grave: false,
      perche: () => t('Un’utenza attiva che non ha mai fatto un accesso è stata creata in anticipo o è rimasta da una prova: ha spesso la password iniziale, nota a chi l’ha creata.'),
      come: [
        t('Disattivala finché non serve; crea le utenze quando la persona arriva.'),
        t('Imposta «cambio password al primo accesso» per le utenze nuove.'),
        t('Se è un account di servizio, documentalo come tale nella scheda della persona (Tipo: account di servizio).'),
      ],
    },
    {
      id: 'password_never_expires', titolo: t('Password che non scade'), grave: false,
      perche: () => t('Una password che non scade resta valida per anni: se è stata vista, scritta o riusata altrove, vale ancora. È il primo bersaglio di chi ha rubato un elenco di credenziali.'),
      come: [
        t('Per le persone togli «La password non scade mai» e lascia lavorare la politica delle password del dominio.'),
        t('Per i servizi usa gMSA (password ruotata da Active Directory) o una rotazione programmata con un gestore di segreti.'),
        t('Dove proprio deve restare, password lunga (almeno 25 caratteri), accesso limitato ai sistemi che servono e nessun privilegio in più.'),
      ],
    },
    {
      id: 'password_old', titolo: t('Password vecchie'), grave: false,
      perche: (p) => t('Una password invariata da oltre {n} giorni è probabilmente nota a più persone di quante dovrebbero, o riusata altrove.', { n: p.password_max_days }),
      come: [
        t('Chiedi il cambio password a queste utenze.'),
        t('Con MFA e password lunghe la scadenza può essere lunga, ma non assente: imposta un massimo nella politica del dominio.'),
        t('Controlla che le password non compaiano in elenchi di credenziali trafugate (per esempio con la protezione password di Entra ID).'),
      ],
    },
    {
      id: 'disabled_privileged', titolo: t('Disattivate ma privilegiate'), grave: true,
      perche: () => t('Un’utenza disattivata ma ancora in un gruppo privilegiato torna amministratore nell’istante in cui qualcuno la riattiva, per errore o di proposito.'),
      come: [
        t('Quando disattivi un’utenza, toglila anche dai gruppi privilegiati (Domain Admins, Administrators, gli Operators).'),
        t('Rivedi i membri dei gruppi privilegiati almeno ogni trimestre: CENSO ACCESS li elenca in «Privilegi».'),
      ],
    },
    {
      id: 'locked', titolo: t('Bloccate'), grave: false,
      perche: () => t('Un’utenza bloccata ha superato i tentativi di password sbagliata: una password vecchia rimasta in un servizio o su un telefono, oppure qualcuno che prova a indovinarla.'),
      come: [
        t('Guarda gli accessi falliti dell’utenza e i Rilevamenti (forza bruta, password spray) prima di sbloccarla.'),
        t('Se è un servizio, aggiorna la password dove è salvata.'),
      ],
    },
  ]
}
